import axios from 'axios';

import { Location } from '@/common/models';
import Exception from '@/common/models/Exception';
import Filter from '@/common/models/query-params/Filter';
import FilterCollection from '@/common/models/query-params/FilterCollection';
import * as ComparisonOperator from '@/common/models/query-params/FilterComparisonOperator';
import QueryParams from '@/common/models/query-params/QueryParams';
import exchAppApi from '@/exch-app/api';
import { HereAppCode, HereAppID, isPointInsidePolygon } from '@/globals';
import orgAppApi from '@/org-app/api';
import rootState from '@/store';
import LatLngBounds = google.maps.LatLngBounds;
import { MapboxApi } from '@/common/apis/MapboxApi';

interface IPlace {
  position: number[];
  geoJson: string;
  id: string;
}

enum SearchTermSeparator {
  In = ' in ',
  Near = ' near ',
}

export default class PlaceSearchEngine {
  places: IPlace[] = [];

  searchTerm: string = '';

  requestsCountLimit: number;

  requestsCount: number;

  completedRequestsCount: number;

  resolveCallback: () => void;

  rejectCallback: () => void;

  cacheKey: string;

  constructor() {
    this.places = [];
    this.searchTerm = '';
    this.requestsCountLimit = 500;
    this.requestsCount = 0;
    this.completedRequestsCount = 0;
    this.resolveCallback = null;
    this.rejectCallback = null;
    this.cacheKey = null;
  }

  static get hasChosenExchange(): boolean {
    return rootState?.getters?.hasChosenExchange || false;
  }

  increaseRequestsCount(): boolean {
    if (this.requestsCount < this.requestsCountLimit) {
      this.requestsCount += 1;
    }

    return this.requestsCount < this.requestsCountLimit;
  }

  increaseCompletedRequestsCount(): boolean {
    this.completedRequestsCount += 1;
    return this.requestsCount === this.completedRequestsCount;
  }

  splitSearchTerm(): { placeName: string; area: string } {
    if (this.searchTerm.indexOf(SearchTermSeparator.In) > -1) {
      return {
        placeName: this.searchTerm.split(SearchTermSeparator.In)[0],
        area: this.searchTerm.split(SearchTermSeparator.In)[1],
      };
    }

    if (this.searchTerm.indexOf(SearchTermSeparator.Near) > -1) {
      return {
        placeName: this.searchTerm.split(SearchTermSeparator.Near)[0],
        area: this.searchTerm.split(SearchTermSeparator.Near)[1],
      };
    }

    return { placeName: null, area: null };
  }

  async mapResultsToBuilding(places: IPlace[]): Promise<IPlace[]> {
    const coordinates = [...places].map((x) => [x.position[1], x.position[0]]);
    const result = await orgAppApi.instance.getBuildingsAtCoordinates(coordinates);

    result.forEach((x: string, i: number) => {
      if (x != null) {
        places[i].geoJson = x;
      }
    });

    return places;
  }

  static splitViewport(viewport: LatLngBounds): LatLngBounds[] {
    const w = viewport.getSouthWest().lng();
    const s = viewport.getSouthWest().lat();
    const e = viewport.getNorthEast().lng();
    const n = viewport.getNorthEast().lat();

    // Split the viewport into 4.
    const midLng = w + (e - w) / 2;
    const midLat = s + (n - s) / 2;

    const viewports = [
      new window.google.maps.LatLngBounds({ lng: midLng, lat: midLat }, { lng: e, lat: n }),
      new window.google.maps.LatLngBounds({ lng: w, lat: midLat }, { lng: midLng, lat: n }),
      new window.google.maps.LatLngBounds({ lng: midLng, lat: s }, { lng: e, lat: midLat }),
      new window.google.maps.LatLngBounds({ lng: w, lat: s }, { lng: midLng, lat: midLat }),
    ];

    return viewports;
  }

  async getNextPage(data: { next: unknown; items: unknown[] }, addressFilter: string): Promise<boolean> {
    if (data.next != null) {
      if (!this.increaseRequestsCount()) {
        return false;
      }

      const successResponse = await axios.get(`${data.next}&addressFilter=${addressFilter}`).catch((err) => {
        throw err;
      });
      this.places = [
        ...this.places,
        ...successResponse.data.items.filter((x) => this.places.find((p) => p.id === x.id) == null),
      ];
      const hasReachedMaxResults = await this.getNextPage(successResponse.data, addressFilter);
      this.increaseCompletedRequestsCount();
      return hasReachedMaxResults;
    }

    return data.items.length === 100;
  }

  async findPlacesInViewport(level: number, placeName: string, viewport: LatLngBounds, addressFilter: string) {
    if (!this.increaseRequestsCount()) {
      return;
    }

    const successResponse = await axios.request({
      url: 'https://places.cit.api.here.com/places/v1/discover/search',
      params: {
        app_id: HereAppID,
        app_code: HereAppCode,
        in: `${viewport.getSouthWest().lng()},${viewport.getSouthWest().lat()},${viewport.getNorthEast().lng()},${viewport.getNorthEast().lat()}`,
        q: placeName,
        size: 100,
        addressFilter,
        recd: false,
        tf: 'plain',
        cs: 'places',
      },
    });

    if (
      (successResponse == null || (level === 1 && successResponse.data.results.items.length === 0)) &&
      addressFilter != null
    ) {
      return;
    }

    this.places = [
      ...this.places,
      ...successResponse.data.results.items.filter((x) => this.places.find((p) => p.id === x.id) == null),
    ];

    const hasReachedMaxResults = await this.getNextPage(successResponse.data.results, addressFilter);

    if (hasReachedMaxResults) {
      // Split the viewport into 4.
      const viewports = PlaceSearchEngine.splitViewport(viewport);
      await this.findPlacesInViewport(1, placeName, viewports[0], addressFilter);
      await this.findPlacesInViewport(1, placeName, viewports[1], addressFilter);
      await this.findPlacesInViewport(1, placeName, viewports[2], addressFilter);
      await this.findPlacesInViewport(1, placeName, viewports[3], addressFilter);
    }
    this.increaseCompletedRequestsCount();
  }

  async searchPlaces(searchTerm: string, usePolygons = true): Promise<IPlace[]> {
    this.requestsCount = 0;
    this.completedRequestsCount = 0;
    this.searchTerm = searchTerm;

    if (searchTerm == null || searchTerm.trim() === '') {
      throw new Exception('Invalid search term');
    }

    const { placeName, area } = this.splitSearchTerm();

    if (placeName == null && area === null) {
      return [];
    }

    this.places.splice(0, this.places.length);

    const mapboxApi = MapboxApi.getInstance();
    const mapBoxResult = await mapboxApi.forwardGeocode({ place: area });

    const [firstResult] = mapBoxResult.features;

    if (firstResult == null) {
      return [];
    }

    let addressFilter = null;
    let locationLevel = null;
    const { context } = firstResult.properties;

    if (context.place != null) {
      const { name } = context.place;
      addressFilter = `city=${name}`;
      locationLevel = 3;
    } else if (context.postcode != null) {
      const { name } = context.postcode;
      addressFilter = `zipCode=${name}`;
    } else if (context.region != null) {
      const { region_code } = context.region;
      addressFilter = `stateCode=${region_code}`;
    } else if (context.district != null) {
      const { name } = context.district;
      addressFilter = `district=${name}`;
      locationLevel = 1;
    } else if (context.country != null) {
      const { country_code } = context.country;
      addressFilter = `countryCode=${country_code}`;
      locationLevel = 0;
    }

    let locationResult: { items: Location[] } = null;
    if (locationLevel != null) {
      const queryParams = QueryParams.for({
        filterCollection: new FilterCollection([
          new Filter('name', [area], ComparisonOperator.IN),
          new Filter('level', [locationLevel]),
        ]),
        expand: ['shapes', 'archived'],
      });

      locationResult = PlaceSearchEngine.hasChosenExchange
        ? await exchAppApi.instance.getLocations({ queryParams })
        : await orgAppApi.instance.getLocationsAsync({ queryParams });
    }

    const { bbox } = firstResult.properties;

    const viewport = new window.google.maps.LatLngBounds(
      { lng: bbox[0], lat: bbox[1] }, // SouthWest:
      { lng: bbox[2], lat: bbox[3] }, // NorthEast:
    );

    await this.findPlacesInViewport(1, placeName, viewport, addressFilter);

    const w = viewport.getSouthWest().lng();
    const s = viewport.getSouthWest().lat();
    const e = viewport.getNorthEast().lng();
    const n = viewport.getNorthEast().lat();

    if (this.places.length < 10 && Math.abs(e - w) > 1 && Math.abs(n - s) > 1) {
      this.places = [];
      this.requestsCount = 0;
      this.completedRequestsCount = 0;

      const viewports = PlaceSearchEngine.splitViewport(viewport);
      await Promise.all([
        this.findPlacesInViewport(1, placeName, viewports[0], addressFilter),
        this.findPlacesInViewport(1, placeName, viewports[1], addressFilter),
        this.findPlacesInViewport(1, placeName, viewports[2], addressFilter),
        this.findPlacesInViewport(1, placeName, viewports[3], addressFilter),
      ]);
    }

    // Filter out places outside of the location polygon shapes.
    const locationsInShape =
      this.places.length > 0
        ? locationResult?.items?.filter((l) => l.shapes != null && l.shapes.length > 0 && !l.archived) || []
        : [];

    if (locationsInShape.length > 0) {
      let placesInShapes = [];
      locationsInShape.forEach((location) => {
        location.shapes.forEach((shape) => {
          if (shape.geometry.type === 'MultiPolygon') {
            shape.geometry.coordinates.forEach((polygon) => {
              const [coordinates] = polygon;
              placesInShapes = [
                ...placesInShapes,
                ...this.places.filter((p) => isPointInsidePolygon([p.position[1], p.position[0]], coordinates)),
              ];
            });
          } else if (shape.geometry.type === 'Polygon') {
            shape.geometry.coordinates.forEach((coordinates) => {
              placesInShapes = [
                ...placesInShapes,
                ...this.places.filter((p) => isPointInsidePolygon([p.position[1], p.position[0]], coordinates)),
              ];
            });
          }
        });
      });

      if (placesInShapes.length > 0) {
        this.places = placesInShapes;
      }
    }

    return !usePolygons ? this.places : await this.mapResultsToBuilding(this.places);
  }
}
